/**
 * Authentication API Module - User Authentication and Profile Management
 * Extracted from monolithic silo-api.ts with enhanced auth patterns from FRONTEND_API_INTEGRATION_GUIDE.md
 */

import { type RequestFunction } from './types'
import type {
  User,
  UserProfile,
  ProfileUpdateInput,
  NotificationPreferences,
  NotificationPreferencesUpdateInput,
  AuthTokenResponse,
} from './types'

// Authentication-specific interfaces
interface AuthTokens {
  access: string;
  refresh: string;
}

interface LoginCredentials {
  username: string; // Can be email or username
  password: string;
  grant_type?: string;
}

export class AuthApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // AUTHENTICATION METHODS (from FRONTEND_API_INTEGRATION_GUIDE.md)
  // ============================================================================

  async login(username: string, password: string): Promise<AuthTokens> {
    const credentials: LoginCredentials = {
      username, // Can be email or username
      password,
      grant_type: 'password' // Specify grant type
    }

    try {
      const data = await this.request<AuthTokenResponse>('/api/auth/login/', {
        method: 'POST',
        body: JSON.stringify(credentials),
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      // Tokens are also set as httpOnly cookies by the backend
      return {
        access: data.access_token,
        refresh: data.refresh_token || ''
      }
    } catch (error: any) {
      throw new Error(error.detail || 'Invalid credentials')
    }
  }

  async refreshToken(): Promise<string> {
    try {
      // Backend reads refresh token from cookie automatically
      const data = await this.request<AuthTokenResponse>('/api/auth/refresh/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      return data.access_token
    } catch (error: any) {
      throw new Error('Failed to refresh token')
    }
  }

  async logout(): Promise<void> {
    await this.request('/api/auth/logout/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
  }

  async verifyToken(): Promise<boolean> {
    try {
      await this.request('/api/auth/verify/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      })
      return true
    } catch (error: any) {
      return false
    }
  }

  // ============================================================================
  // USER EXISTENCE CHECK
  // ============================================================================

  async checkUserExists(email: string): Promise<{ exists: boolean; user?: User }> {
    return this.request('/api/users/check-email/', { 
      params: { email }
    })
  }

  // ============================================================================
  // USER PROFILE MANAGEMENT (Portal)
  // ============================================================================

  async getUserProfile(): Promise<UserProfile> {
    return this.request('/api/users/profile/', {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
  }

  async updateUserProfile(data: ProfileUpdateInput): Promise<UserProfile> {
    // Use the CORRECT endpoint as per backend team - no userId needed
    const response = await this.request('/api/users/profile/', {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
    
    console.log('[Auth API] ===== UPDATE PROFILE RESPONSE =====')
    console.log('[Auth API] Full response:', response)
    console.log('[Auth API] Response keys:', Object.keys(response || {}))
    
    // The response might BE the profile directly, not wrapped in message/profile
    // Let's check both formats
    if (response.profile) {
      console.log('[Auth API] Using response.profile format')
      return response.profile
    } else if (response.id) {
      console.log('[Auth API] Using direct profile format')
      return response
    } else {
      console.error('[Auth API] Unexpected response format:', response)
      throw new Error('Invalid response format from server')
    }
  }

  // ============================================================================
  // PASSWORD MANAGEMENT (Portal)
  // ============================================================================

  async changePassword(data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<{ success: true; message: string; timestamp: string }> {
    return this.request('/api/users/change-password/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async checkPasswordStrength(password: string): Promise<{
    strength: {
      score: number;
      level: 'weak' | 'fair' | 'good' | 'strong';
      max_score: number;
      requirements_met: string;
    };
    requirements: {
      length: boolean;
      uppercase: boolean;
      lowercase: boolean;
      numbers: boolean;
      special_chars: boolean;
    };
    feedback: string[];
    django_validation_passed: boolean;
  }> {
    return this.request('/api/users/check-password-strength/', {
      method: 'POST',
      body: JSON.stringify({ password })
    })
  }

  async getSecuritySettings(): Promise<{
    user_id: number;
    username: string;
    email: string;
    password_info: {
      last_changed: string | null;
      age_days: number | null;
      requires_change: boolean;
    };
    login_info: {
      last_login: string;
      date_joined: string;
      is_active: boolean;
    };
    security_features: {
      password_change: boolean;
      password_strength_validation: boolean;
      current_password_verification: boolean;
      email_notifications: boolean;
      two_factor_authentication: boolean;
    };
  }> {
    return this.request('/api/users/security-settings/')
  }

  // ============================================================================
  // NOTIFICATION PREFERENCES (Portal)
  // ============================================================================

  async getNotificationPreferences(contactId?: string): Promise<NotificationPreferences> {
    // For CRM contacts, use the contact-based endpoint
    if (contactId) {
      return await this.request(`/api/crm/contacts/${contactId}/notification-preferences/`)
    }
    
    // TODO: Backend endpoint /api/users/notification-preferences/ does not exist yet
    // This endpoint needs to be implemented to allow portal users to manage their preferences
    // For now, we'll use localStorage as a temporary solution
    
    // Check if we have locally stored preferences
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('notification_preferences')
      if (stored) {
        try {
          return JSON.parse(stored)
        } catch (e) {
          // Invalid JSON, continue to defaults
        }
      }
    }
    
    // Return sensible defaults for portal users
    const defaults: NotificationPreferences = {
      email_enabled: true,
      sms_enabled: false,
      push_enabled: false,
      order_updates: true,
      marketing: false,
      newsletter: false
    }
    
    // Store defaults for consistency
    if (typeof window !== 'undefined') {
      localStorage.setItem('notification_preferences', JSON.stringify(defaults))
    }
    
    return defaults
  }

  async updateNotificationPreferences(data: NotificationPreferencesUpdateInput, contactId?: string): Promise<NotificationPreferences> {
    // For CRM contacts, use the contact-based endpoint
    if (contactId) {
      return await this.request(`/api/crm/contacts/${contactId}/notification-preferences/`, {
        method: 'PUT',
        body: JSON.stringify(data)
      })
    }
    
    // TODO: Backend endpoint /api/users/notification-preferences/ does not exist yet
    // This endpoint needs to be implemented to allow portal users to save their preferences
    // For now, we'll save to localStorage as a temporary solution
    
    // Get current preferences
    let current: NotificationPreferences = {
      email_enabled: true,
      sms_enabled: false,
      push_enabled: false,
      order_updates: true,
      marketing: false,
      newsletter: false
    }
    
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('notification_preferences')
      if (stored) {
        try {
          current = JSON.parse(stored)
        } catch (e) {
          // Invalid JSON, use defaults
        }
      }
      
      // Merge with new data
      const updated = { ...current, ...data }
      
      // Save to localStorage
      localStorage.setItem('notification_preferences', JSON.stringify(updated))
      
      // Log that this is temporary
      console.info('Notification preferences saved locally. Backend endpoint not yet implemented.')
      
      return updated
    }
    
    // If no localStorage available, just return merged data
    return { ...current, ...data }
  }

  // ============================================================================
  // PORTAL ACCOUNT MANAGEMENT (moved from CRM for better organization)
  // ============================================================================

  async createPortalAccount(contactId: number, email: string): Promise<{ success: boolean; user: User }> {
    return this.request(`/api/crm/contacts/${contactId}/create-portal-account/`, {
      method: 'POST',
      body: JSON.stringify({ 
        email,
        send_invitation_email: true 
      })
    })
  }

  async sendPortalInvite(contactId: number, email?: string): Promise<{ 
    success: boolean
    message: string
    invite_status?: 'sent' | 'resent' | 'already_accepted'
    user?: User 
  }> {
    return this.request(`/api/crm/contacts/${contactId}/send-portal-invite/`, {
      method: 'POST',
      body: JSON.stringify({ 
        email: email,
        resend: false 
      })
    })
  }

  async resendPortalInvite(contactId: number): Promise<{ 
    success: boolean
    message: string
    invite_status: 'resent'
  }> {
    return this.request(`/api/crm/contacts/${contactId}/send-portal-invite/`, {
      method: 'POST',
      body: JSON.stringify({ 
        resend: true 
      })
    })
  }

  async getPortalInviteStatus(contactId: number): Promise<{
    has_account: boolean
    invite_sent: boolean
    invite_accepted: boolean
    user_email?: string
    invite_sent_at?: string
    invite_accepted_at?: string
  }> {
    try {
      const contact = await this.request(`/api/crm/contacts/${contactId}/`)
      
      // Extract portal status information from the contact response
      return {
        has_account: !!contact.user_id || !!contact.portal_user,
        invite_sent: !!contact.portal_invite_sent || !!contact.invite_sent_at,
        invite_accepted: !!contact.portal_invite_accepted || !!contact.invite_accepted_at,
        user_email: contact.portal_user_email || contact.user?.email,
        invite_sent_at: contact.invite_sent_at,
        invite_accepted_at: contact.invite_accepted_at
      }
    } catch (error: any) {
      // Fallback: return default status if contact fetch fails
      return {
        has_account: false,
        invite_sent: false,
        invite_accepted: false
      }
    }
  }

  async linkExistingUserToContact(contactId: number, userId: number): Promise<any> {
    return this.request(`/api/crm/contacts/${contactId}/link-user/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    })
  }

  async unlinkContactFromUser(contactId: number): Promise<any> {
    return this.request(`/api/crm/contacts/${contactId}/unlink-user/`, {
      method: 'POST'
    })
  }

  // ============================================================================
  // AUTHENTICATION STATE MANAGEMENT
  // ============================================================================

  /**
   * Check if current user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      await this.getUserProfile()
      return true
    } catch (error: any) {
      return false
    }
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<UserProfile | null> {
    try {
      return await this.getUserProfile()
    } catch (error: any) {
      return null
    }
  }

  /**
   * Check if current user has admin/staff privileges
   */
  async isStaffUser(): Promise<boolean> {
    try {
      const profile = await this.getUserProfile()
      // Check if profile has staff-only fields or properties
      return !!(profile as any).is_staff || !!(profile as any).is_superuser
    } catch (error: any) {
      return false
    }
  }
}